import { api } from '../../core/network/api';
import { observe as observeLevel } from './level-up';
import { t } from '../i18n';

export type HeroClass = 'WARRIOR' | 'PALADIN' | 'ARCHER' | 'MAGE';

export const HERO_CLASSES: readonly HeroClass[] = ['WARRIOR', 'PALADIN', 'ARCHER', 'MAGE'];

/**
 * Display name for a class. The enum key is what the server and the battle
 * balance speak; this is only what the player reads, so it goes through the
 * catalogue like any other copy.
 *
 * A function rather than the old `CLASS_LABELS` record: a record would be built
 * once at module load and then keep showing the tongue that was active at boot.
 * Called inside a template, this re-resolves whenever the locale changes.
 *
 * Most of these names stay put across locales — Berserker, Huskarl and Völva are
 * borrowed Norse words, not English ones — so the glossary keeps them and lets
 * only ARCHER ("Hunter") actually translate.
 */
export const classLabel = (c: HeroClass): string => t(`classes.${c}.name`);

export interface HeroProfile {
  hero: {
    id: string;
    name: string;
    class: HeroClass;
    stats: { hp: number; maxHp: number; attack: number; defense: number; speed: number; magic: number };
    nerve: { current: number; max: number };
    position: { col: number; row: number };
    /** Fractional regen anchors + rates for client-side countdowns. */
    regen: {
      hp: { exact: number; perMin: number };
      nerve: { exact: number; perMin: number };
    };
  };
  progression: {
    username: string | null;
    level: number;
    xp: number;
    cash: number;
    goldBars: number;
  };
}

/**
 * GET /api/heroes/me — returns null if the player has no hero yet (404).
 *
 * Every profile read goes through here, so this is where the level watcher is
 * fed (QA 86cb91hh1). Wiring it into a screen's refresh instead looked simpler
 * and was wrong: HeroScreen reads the profile from TWO places (mount and
 * refresh), so the mount bypassed the hook and the watcher had no baseline.
 * One seam, every caller, no faucet and no screen can forget.
 */
export async function fetchHero(): Promise<HeroProfile | null> {
  try {
    const profile = await api.get<HeroProfile>('/heroes/me');
    observeLevel(profile?.progression?.level);
    return profile;
  } catch (e) {
    if ((e as { status?: number }).status === 404) return null;
    throw e;
  }
}

/** POST /api/heroes — create the account's hero. */
export function createHero(name: string, cls: HeroClass): Promise<HeroProfile> {
  return api.post<HeroProfile>('/heroes', { name, class: cls });
}

export interface EffectiveStats {
  base: { attack: number; defense: number; magic: number; speed: number; hp: number; maxHp: number };
  gear: { attack: number; defense: number; magic: number; speed: number; hp: number };
  effective: { attack: number; defense: number; magic: number; speed: number; maxHp: number };
  sets: { name: string; pieces: number; description: string }[];
  boosts: { cashRush: boolean; xpBooster: boolean; revive: boolean };
}

/** GET /api/heroes/stats — effective combat power (base + gear), sets, boosts. */
export const fetchStats = () => api.get<EffectiveStats>('/heroes/stats');

/** Cosmetic XP curve for the Level progress bar — mirrors core's
 *  `economy/xp-curve.ts` (base `XP_PER_LEVEL` 100, growth `XP_STEP_GROWTH`
 *  25). The requirement GROWS with level now (QA 86cb91hh1) — the flat
 *  `xp % 100` bar would drift off the server's level within one level of the
 *  curve kicking in. Never used for combat math, purely display. */
export const XP_STEP_BASE = 100;
export const XP_STEP_GROWTH = 25;

/** Cumulative XP needed to REACH `level` — core's `totalXpForLevel`, mirrored. */
export function totalXpForLevel(level: number): number {
  const n = Math.max(1, level) - 1;
  return XP_STEP_BASE * n + (XP_STEP_GROWTH * n * (n - 1)) / 2;
}

/** Current-level XP progress from cumulative XP: how far into the level and
 *  what the step out of it costs. Level is derived from the same curve the
 *  server levels with, so the bar can never disagree with the plate. */
export function xpProgress(xp: number): { current: number; needed: number } {
  let level = 1;
  while (totalXpForLevel(level + 1) <= xp) level += 1;
  return {
    current: Math.max(0, xp - totalXpForLevel(level)),
    needed: XP_STEP_BASE + XP_STEP_GROWTH * (level - 1),
  };
}

// ── Settings → danger zone: delete / reroll the hero ────────────────────────

export interface RerollQuote {
  /** Σ currentPrice of every SILVER-priced artifact owned (equipped included). */
  inventoryValue: number;
  /** floor(inventoryValue * 0.20) — the silver retiring pays out. */
  compensation: number;
  /** Σ currentPrice of every RUNE-priced artifact owned — a separate unit, never summed with silver. */
  inventoryValuePremium: number;
  /** floor(inventoryValuePremium * 0.20) — the runes retiring pays out. */
  compensationPremium: number;
}

/** GET /api/heroes/reroll-quote — the payout preview shown before either danger-zone action. */
export const fetchRerollQuote = () => api.get<RerollQuote>('/heroes/reroll-quote');

export type RetireResult = RerollQuote & {
  mode: 'delete' | 'reroll';
  /** The freshly-created hero when mode='reroll'; null for 'delete'. */
  hero: HeroProfile | null;
};

/** POST /api/heroes/retire — wipes the hero + every owned artifact, pays the
 *  20% compensation, and (mode='reroll' only) stands up a fresh hero of `cls`. */
export function retireHero(mode: 'delete'): Promise<RetireResult>;
export function retireHero(mode: 'reroll', cls: HeroClass, name?: string): Promise<RetireResult>;
export function retireHero(mode: 'delete' | 'reroll', cls?: HeroClass, name?: string): Promise<RetireResult> {
  return api.post<RetireResult>('/heroes/retire', mode === 'reroll' ? { mode, class: cls, name } : { mode });
}
